import React from 'react';
import { formatShortDate, formatCount } from '../lib/format';

/**
 * Plain-SVG sparkline of a numeric series — no chart library.
 * `series` is the backend's daily rows ({ date, activeCount, … }); `field`
 * picks the value. Fewer than two points → "Historikk kommer" placeholder,
 * since the daily snapshot job may not have produced history yet.
 */
const W = 240;
const H = 56;
const PAD = 4;

export function toPoints(series, field, width = W, height = H, pad = PAD) {
  const values = series.map(row => Number(row?.[field]) || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const stepX = (width - pad * 2) / (values.length - 1);
  return values.map((v, i) => ({
    x: pad + i * stepX,
    y: height - pad - ((v - min) / span) * (height - pad * 2),
    v
  }));
}

export default function Sparkline({ series, field = 'activeCount', label = 'Trend' }) {
  const rows = Array.isArray(series) ? series : [];
  if (rows.length < 2) {
    return (
      <div className="admin-sparkline admin-sparkline--empty" role="img" aria-label={`${label}: historikk kommer`}>
        <span>Historikk kommer</span>
      </div>
    );
  }

  const pts = toPoints(rows, field);
  const line = pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `${PAD},${H - PAD} ${line} ${pts[pts.length - 1].x.toFixed(1)},${H - PAD}`;
  const last = pts[pts.length - 1];
  const first = rows[0]?.date;
  const latest = rows[rows.length - 1]?.date;

  return (
    <div className="admin-sparkline">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${label}: ${formatCount(pts[0].v)} til ${formatCount(last.v)}`}
      >
        <polygon points={area} fill="rgba(217,119,87,0.14)" />
        <polyline
          points={line}
          fill="none"
          stroke="#D97757"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        <circle cx={last.x} cy={last.y} r="3" fill="#D97757" stroke="#FFFFFF" strokeWidth="1.5" />
      </svg>
      <div className="admin-sparkline__axis" aria-hidden="true">
        <span>{formatShortDate(first)}</span>
        <span>{formatShortDate(latest)}</span>
      </div>
    </div>
  );
}
